import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Button, Card, KpiCard, Spinner } from '@kss/ui';
import {
  AlertTriangle, Download, FileText, Filter, IndianRupee, Printer, Receipt, RefreshCw, Search, Wallet,
} from 'lucide-react';
import { pickList, portalApi, PortalApiError } from './portalApi';
import { usePortalSession } from './portalSession';
import { printInvoice, type InvoiceLine } from '../../../panel/src/print/templates';
import { printQrUrl } from '../../../panel/src/print/branding';

/**
 * Customer-facing invoice list. The customer sees only their own
 * invoices (server-side filter via `customerId` query) and can:
 *   - Search / filter by status
 *   - Print any invoice (uses the same template as the staff panel)
 *   - Pay any open invoice — stubbed `/sales/SalesInvoice/{id}/pay-link`
 *     which returns a Razorpay/Stripe checkout URL when wired up.
 *
 * The Dashboard's "Pay now" CTA links here with `?focus=<invoiceId>`
 * which auto-scrolls + opens the pay drawer for that row.
 */

interface InvoiceRow {
  id?: number | string;
  invoiceId?: string;
  invoiceNumber?: string;
  invoiceDate?: string;
  dueDate?: string;
  customerName?: string;
  totalAmount?: number;
  balanceAmount?: number;
  paidAmount?: number;
  taxAmount?: number;
  cgstAmount?: number;
  sgstAmount?: number;
  igstAmount?: number;
  subTotal?: number;
  totalDiscount?: number;
  roundOff?: number;
  status?: string;
  notes?: string;
  terms?: string;
  irn?: string;
  ewayBillNo?: string;
}

interface InvoiceLineRow {
  description?: string;
  productName?: string;
  hsnCode?: string;
  quantity?: number;
  qty?: number;
  unit?: string;
  rate?: number;
  unitPrice?: number;
  discountPct?: number;
  taxRate?: number;
  gstRate?: number;
  totalAmount?: number;
  total?: number;
}

interface TenantInfo {
  name?: string;
  businessName?: string;
  address?: string;
  city?: string;
  state?: string;
  pincode?: string;
  gstin?: string;
  pan?: string;
  phone?: string;
  email?: string;
  website?: string;
  // Branding columns from /tenants/Tenant/me — so the portal print carries the
  // same logo / signature / QR / bank as the staff panel prints.
  logoUrl?: string;
  signatureImageUrl?: string;
  qrImageUrl?: string;
  bankName?: string;
  bankAccountName?: string;
  bankAccountNo?: string;
  bankIfsc?: string;
  bankBranch?: string;
}

const STATUS_FILTERS = ['All', 'Open', 'Overdue', 'Paid'] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number];

export default function PortalInvoices() {
  const session = usePortalSession();
  const [params, setParams] = useSearchParams();
  const focusId = params.get('focus') ?? '';

  const [rows, setRows] = useState<InvoiceRow[]>([]);
  const [tenant, setTenant] = useState<TenantInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('All');
  const [paying, setPaying] = useState<string | null>(null);
  const [printing, setPrinting] = useState<string | null>(null);
  const focusRowRef = useRef<HTMLDivElement | null>(null);

  async function load() {
    if (!session?.customerId) return;
    setLoading(true);
    setErr(null);
    try {
      const [inv, t] = await Promise.all([
        portalApi.get<unknown>('/sales/SalesInvoice', { query: { customerId: session.customerId } }),
        portalApi.get<TenantInfo>('/tenants/Tenant/me').catch(() => null),
      ]);
      setRows(pickList<InvoiceRow>(inv));
      if (t) setTenant(t);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [session?.customerId]);

  // Auto-scroll to the focused invoice once it's in the DOM.
  useEffect(() => {
    if (!focusId || !focusRowRef.current) return;
    focusRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [focusId, rows.length]);

  const kpis = useMemo(() => {
    const open = rows.filter((r) => isOpen(r.status));
    const outstanding = open.reduce((s, r) => s + Number(r.balanceAmount ?? r.totalAmount ?? 0), 0);
    const overdue = open.filter((r) => {
      if (!r.dueDate) return false;
      const t = Date.parse(String(r.dueDate));
      return !Number.isNaN(t) && t < Date.now();
    }).length;
    const paid = rows.filter((r) => String(r.status ?? '').toLowerCase() === 'paid').length;
    return { outstanding, overdue, paid, total: rows.length };
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      .filter((r) => {
        if (statusFilter === 'Open' && !isOpen(r.status)) return false;
        if (statusFilter === 'Paid' && String(r.status ?? '').toLowerCase() !== 'paid') return false;
        if (statusFilter === 'Overdue') {
          if (!isOpen(r.status) || !r.dueDate) return false;
          const t = Date.parse(String(r.dueDate));
          if (Number.isNaN(t) || t >= Date.now()) return false;
        }
        if (q) {
          const hay = `${r.invoiceNumber ?? ''} ${r.invoiceId ?? ''} ${r.notes ?? ''}`.toLowerCase();
          if (!hay.includes(q)) return false;
        }
        return true;
      })
      .sort((a, b) => Date.parse(String(b.invoiceDate ?? '')) - Date.parse(String(a.invoiceDate ?? '')));
  }, [rows, search, statusFilter]);

  async function handlePrint(r: InvoiceRow) {
    if (!session) return;
    const id = String(r.invoiceId ?? r.id ?? '');
    setPrinting(id);
    try {
      // Pull the full invoice so the line-level breakdown is accurate.
      let lines: InvoiceLineRow[] = [];
      try {
        const ls = await portalApi.get<unknown>(`/sales/SalesInvoiceLine`, { query: { invoiceId: id } });
        lines = pickList<InvoiceLineRow>(ls);
      } catch {
        lines = [];
      }
      const printLines: InvoiceLine[] = lines.map((l) => ({
        description: String(l.description ?? l.productName ?? '—'),
        hsnCode: l.hsnCode,
        qty: Number(l.quantity ?? l.qty ?? 0),
        unit: l.unit,
        rate: Number(l.rate ?? l.unitPrice ?? 0),
        discountPct: l.discountPct,
        taxRate: Number(l.taxRate ?? l.gstRate ?? 0),
        total: Number(l.totalAmount ?? l.total ?? 0),
      }));
      const number = String(r.invoiceNumber ?? id);
      const total = Number(r.totalAmount ?? 0);
      printInvoice({
        docType: 'TAX INVOICE',
        number,
        date: String(r.invoiceDate ?? ''),
        dueDate: r.dueDate,
        customerType: 'B2B',
        tenant: {
          name: tenant?.businessName ?? tenant?.name ?? session.tenantCode,
          logoUrl: tenant?.logoUrl || undefined,
          address: tenant?.address,
          city: tenant?.city,
          state: tenant?.state,
          pincode: tenant?.pincode,
          gstin: tenant?.gstin,
          pan: tenant?.pan,
          phone: tenant?.phone,
          email: tenant?.email,
          website: tenant?.website,
        },
        parties: [{
          label: 'Bill to',
          name: session.customerName,
          email: session.email,
          phone: session.phone,
        }],
        lines: printLines,
        subTotal: Number(r.subTotal ?? 0),
        totalDiscount: Number(r.totalDiscount ?? 0),
        taxableAmount: Number(r.subTotal ?? 0) - Number(r.totalDiscount ?? 0),
        cgstAmount: Number(r.cgstAmount ?? 0),
        sgstAmount: Number(r.sgstAmount ?? 0),
        igstAmount: Number(r.igstAmount ?? 0),
        roundOff: Number(r.roundOff ?? 0),
        totalAmount: total,
        notes: r.notes,
        terms: r.terms,
        irn: r.irn,
        ewayBillNo: r.ewayBillNo,
        // Branding: seller bank block, signatory image, and an ALWAYS-present
        // inline QR (was missing → the portal invoice printed with no QR).
        bank: (tenant?.bankName || tenant?.bankAccountNo) ? {
          name: tenant?.bankName, accountName: tenant?.bankAccountName,
          accountNo: tenant?.bankAccountNo, ifsc: tenant?.bankIfsc, branch: tenant?.bankBranch,
        } : undefined,
        signatureUrl: tenant?.signatureImageUrl || undefined,
        qrUrl: printQrUrl(tenant?.qrImageUrl, ['TAX INVOICE', number, session.customerName, 'Amt:' + total, tenant?.gstin]),
      });
    } finally {
      setPrinting(null);
    }
  }

  async function handlePay(r: InvoiceRow) {
    const id = String(r.invoiceId ?? r.id ?? '');
    setPaying(id);
    setErr(null);
    try {
      // Backend hands back a checkout URL (Razorpay/Stripe) when wired.
      const resp = await portalApi.post<{ paymentUrl?: string; url?: string }>(
        `/sales/SalesInvoice/${encodeURIComponent(id)}/pay-link`,
        { amount: r.balanceAmount ?? r.totalAmount },
      );
      const url = resp?.paymentUrl ?? resp?.url;
      if (url) {
        window.location.href = url;
      } else {
        setErr('Payment gateway is not yet configured for this tenant. Please contact support.');
      }
    } catch (e) {
      if (e instanceof PortalApiError && e.status === 404) {
        setErr('Online payments are not enabled by this supplier. Please pay via the bank details on the invoice or contact support.');
      } else {
        setErr(e instanceof Error ? e.message : 'Could not start payment');
      }
    } finally {
      setPaying(null);
    }
  }

  function clearFocus() {
    if (!focusId) return;
    const next = new URLSearchParams(params);
    next.delete('focus');
    setParams(next, { replace: true });
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Your invoices</h2>
          <p className="text-xs text-[var(--color-text-secondary)]">
            View, download or pay any invoice raised against your account.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Reload
        </Button>
      </div>

      {err && <Alert variant="warning">{err}</Alert>}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          title="Outstanding"
          value={`₹ ${inr(kpis.outstanding)}`}
          subtitle={kpis.outstanding > 0 ? 'Pay anytime' : 'Nothing due'}
          icon={<IndianRupee className={`h-5 w-5 ${kpis.outstanding ? 'text-warning' : 'text-success'}`} />}
        />
        <KpiCard
          title="Overdue"
          value={kpis.overdue.toLocaleString()}
          subtitle={kpis.overdue ? 'Past due date' : 'On schedule'}
          icon={<AlertTriangle className={`h-5 w-5 ${kpis.overdue ? 'text-danger' : 'text-success'}`} />}
        />
        <KpiCard
          title="Paid"
          value={kpis.paid.toLocaleString()}
          subtitle="Cleared invoices"
          icon={<Wallet className="h-5 w-5 text-success" />}
        />
        <KpiCard
          title="Total invoices"
          value={kpis.total.toLocaleString()}
          subtitle="All time"
          icon={<FileText className="h-5 w-5 text-info" />}
        />
      </div>

      <Card>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--color-text-muted)]" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by invoice number or note…"
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] py-1.5 pl-7 pr-2 text-sm outline-none focus:border-[var(--color-primary)]"
            />
          </div>
          <div className="inline-flex items-center gap-1 rounded-md border border-[var(--color-border)] p-0.5 text-xs">
            <Filter className="ml-1 h-3 w-3 text-[var(--color-text-muted)]" />
            {STATUS_FILTERS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStatusFilter(s)}
                className={`rounded px-2 py-0.5 transition-colors ${
                  statusFilter === s
                    ? 'bg-[var(--color-primary)] text-white'
                    : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'
                }`}
              >
                {s}
              </button>
            ))}
          </div>
          {focusId && (
            <button
              type="button"
              onClick={clearFocus}
              className="rounded-md border border-[var(--color-border)] px-2 py-1 text-[11px] text-[var(--color-text-secondary)] hover:border-[var(--color-primary)]"
            >
              Clear focus
            </button>
          )}
        </div>

        {loading && filtered.length === 0 ? (
          <div className="flex items-center gap-2 p-6 text-sm text-[var(--color-text-muted)]">
            <Spinner /> Loading invoices…
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center gap-2 p-10 text-center text-sm text-[var(--color-text-muted)]">
            <Receipt className="h-8 w-8 opacity-40" />
            <div>No invoices match the current filter.</div>
            {(search || statusFilter !== 'All') && (
              <button
                type="button"
                onClick={() => { setSearch(''); setStatusFilter('All'); }}
                className="text-xs text-[var(--color-primary)] hover:underline"
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-[var(--color-border)]">
            {filtered.map((r) => {
              const id = String(r.invoiceId ?? r.id ?? '');
              const open = isOpen(r.status);
              const overdue = open && r.dueDate && Date.parse(String(r.dueDate)) < Date.now();
              const isFocused = focusId && focusId === id;
              return (
                <div
                  key={id}
                  ref={isFocused ? focusRowRef : undefined}
                  className={`flex flex-wrap items-center gap-3 py-3 text-sm ${
                    isFocused ? 'rounded-md bg-[var(--color-primary)]/5 px-2 ring-1 ring-[var(--color-primary)]/30' : ''
                  }`}
                >
                  <span className="grid h-9 w-9 place-items-center rounded-md border border-[var(--color-border)] text-[var(--color-text-muted)]">
                    <Receipt size={14} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium">{String(r.invoiceNumber ?? id)}</div>
                    <div className="text-[11px] text-[var(--color-text-muted)]">
                      {fmtDate(r.invoiceDate)}
                      {r.dueDate ? ` · due ${fmtDate(r.dueDate)}` : ''}
                      {overdue ? ' · OVERDUE' : ''}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold tabular-nums">₹ {inr(Number(r.totalAmount ?? 0))}</div>
                    {open && Number(r.balanceAmount ?? 0) > 0 && Number(r.balanceAmount ?? 0) !== Number(r.totalAmount ?? 0) && (
                      <div className="text-[11px] text-warning">
                        Balance ₹ {inr(Number(r.balanceAmount ?? 0))}
                      </div>
                    )}
                    <StatusPill v={String(r.status ?? '')} overdue={!!overdue} />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePrint(r)}
                      disabled={printing === id}
                      title="Print or save as PDF"
                    >
                      {printing === id ? <Spinner /> : <Printer className="h-3.5 w-3.5" />}
                      <span className="hidden sm:inline">Print</span>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePrint(r)}
                      title="Download a PDF copy"
                    >
                      <Download className="h-3.5 w-3.5" />
                      <span className="hidden sm:inline">PDF</span>
                    </Button>
                    {open && (
                      <Button
                        size="sm"
                        onClick={() => handlePay(r)}
                        disabled={paying === id}
                      >
                        {paying === id ? <Spinner /> : <IndianRupee className="h-3.5 w-3.5" />}
                        Pay
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}

function StatusPill({ v, overdue }: { v: string; overdue: boolean }) {
  const s = v.toLowerCase();
  let label = v || '—';
  let cls = 'border-content-muted/40 bg-content-muted/10 text-content-muted';
  if (overdue) { cls = 'border-danger/40 bg-danger/10 text-danger'; label = 'Overdue'; }
  else if (s === 'paid') cls = 'border-success/40 bg-success/10 text-success';
  else if (s === 'draft') cls = 'border-warning/40 bg-warning/10 text-warning';
  else if (s === 'posted') cls = 'border-info/40 bg-info/10 text-info';
  return (
    <span className={`mt-1 inline-flex rounded-full border px-2 py-0.5 text-[10px] capitalize ${cls}`}>
      {label}
    </span>
  );
}

function isOpen(status: unknown): boolean {
  const s = String(status ?? '').toLowerCase();
  return s !== 'paid' && s !== 'cancelled' && s !== '';
}

function fmtDate(v: unknown): string {
  if (typeof v !== 'string' || !v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v.slice(0, 10);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function inr(n: number): string {
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}
